import { useEffect, useState } from "react";
import "./styles.css";
import { FaceScan } from "./FaceScan";
import { FaceTutorial } from "./FaceTutorial";
import { TopHeadValidate } from "./TopHeadValidate";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HeadInitialize } from "./HeadInitialize";
import { HeadScan } from "./HeadScan";
import { useConfig } from "../../context/ConfigContext";
import { useScanImage } from "../../context/ImageContext";
import { useUID } from "../../context/UIDContext";
import { useAmplitude } from "../../context/AmplitudeContext";

export const ScanModule = ({
  apiToken,
  faceModel,
  runNextModule = () => {},
  onError = () => {},
}) => {
  const { t } = useTranslation("scanModule");
  const {
    addFrontImage,
    addSideImage,
    validateTopHeadImage,
    getHairRegion,
    uploadImages,
  } = useScanImage();
  const { configData } = useConfig();
  const { uid } = useUID();
  const trackEvent = useAmplitude();

  const [currentStep, updateCurrentStep] = useState(
    configData.FaceModuleFlags.tutorialEnabled ? 0 : 1,
  );
  const [showHeadTutorial, setShowHeadTutorial] = useState(true);

  useEffect(() => {
    if (currentStep === 5) {
      // Cloud API
      const timerId = setTimeout(async () => {
        const uploadRes = await uploadImages(apiToken);
        const hairRes = await getHairRegion(apiToken);

        if (uploadRes && hairRes) {
          trackEvent("webapp_user_scan_images_saved", uid);
          runNextModule();
        } else {
          onError();
        }
      }, 1000);

      return () => clearTimeout(timerId);
    }
  }, [currentStep]);

  const loadNextStep = () => {
    updateCurrentStep((prevState) => {
      if (prevState === 1 && !configData.HeadModuleFlags.isEnabled) {
        return 5;
      } else {
        return prevState + 1;
      }
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <FaceScan
            sideFaceEnabled={configData.FaceModuleFlags.sideScanEnabled}
            addFrontImage={addFrontImage}
            addSideImage={addSideImage}
            runFaceModel={faceModel}
            onComplete={loadNextStep}
            onError={onError}
          />
        );
      case 2:
        return (
          <HeadInitialize
            onSkipClick={() => updateCurrentStep(5)}
            onContinueClick={loadNextStep}
          />
        );
      case 3:
        return (
          <HeadScan
            onBack={showHeadTutorial ? () => updateCurrentStep(2) : undefined}
            onFinish={() => updateCurrentStep(5)}
            addImage={addSideImage}
          />
        );
      case 4:
        return (
          <TopHeadValidate
            onBack={() => {
              setShowHeadTutorial(false);
              updateCurrentStep(3);
            }}
            onContinue={loadNextStep}
            onError={onError}
            validateTopHeadImage={validateTopHeadImage}
          />
        );
      case 5:
        return (
          <div className="scan-module-loading">
            <CircularProgress
              disableShrink
              color="inherit"
              size={40}
              thickness={4}
            />
            <p className="scan-interim-text">
              {configData.FaceModuleFlags.sideScanEnabled
                ? t("upload-images")
                : t("upload-image")}
            </p>
          </div>
        );
      default:
        return (
          <FaceTutorial
            onComplete={loadNextStep}
            sideFaceEnabled={configData.FaceModuleFlags.sideScanEnabled}
          />
        );
    }
  };

  return <div className="scan-module">{renderCurrentStep()}</div>;
};
